/**
 * Iter 2026-02 · My Projects — cross-role assignment landing page.
 *
 * Any user (Developer / Accounts / Support) who has been assigned to
 * one or more projects sees them grouped by their role in each. No
 * `legacy_project:assign` permission required — just being an assignee is
 * enough to view. Route lives under MY WORKSPACE.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Briefcase, Wrench, HandCoins, Headset, ArrowsClockwise,
    Buildings, ArrowRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const ROLE_META = {
    technical: { label: "Technical", icon: Wrench, colour: "text-indigo-700 bg-indigo-50 border-indigo-200" },
    commercial: { label: "Commercial", icon: HandCoins, colour: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    support: { label: "Support", icon: Headset, colour: "text-amber-700 bg-amber-50 border-amber-200" },
};

export default function MyProjects() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [roleFilter, setRoleFilter] = useState("all"); // all | technical | commercial | support

    const load = async () => {
        setLoading(true);
        try {
            const params = roleFilter === "all" ? {} : { role: roleFilter };
            const { data } = await api.get("/billing/project/_meta/my-assignments", { params });
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load your projects"));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [roleFilter]);

    const totalsByRole = useMemo(() => {
        const t = { technical: 0, commercial: 0, support: 0 };
        for (const p of items) {
            for (const r of p.my_roles || []) t[r] = (t[r] || 0) + 1;
        }
        return t;
    }, [items]);

    return (
        <div data-testid="my-projects-page">
            <PageHeader
                eyebrow={<span className="flex items-center gap-1"><Briefcase size={11} /> My Workspace</span>}
                title="My projects"
                description="Every project where you've been assigned as Technical, Commercial or Support owner. Grouped by your role."
                actions={
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={load}
                        className="gap-1.5"
                        data-testid="my-projects-refresh"
                    >
                        <ArrowsClockwise size={13} /> Refresh
                    </Button>
                }
            />
            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Filter strip */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="my-projects-filter-strip">
                    <FilterPill
                        active={roleFilter === "all"}
                        onClick={() => setRoleFilter("all")}
                        label={`All · ${items.length}`}
                        testid="my-projects-filter-all"
                    />
                    {["technical", "commercial", "support"].map((r) => (
                        <FilterPill
                            key={r}
                            active={roleFilter === r}
                            onClick={() => setRoleFilter(r)}
                            label={`${ROLE_META[r].label} · ${totalsByRole[r] || 0}`}
                            icon={ROLE_META[r].icon}
                            testid={`my-projects-filter-${r}`}
                        />
                    ))}
                </div>

                {loading ? (
                    <div className="p-10 text-sm text-slate-500" data-testid="my-projects-loading">Loading…</div>
                ) : items.length === 0 ? (
                    <div
                        className="reedos-card p-12 flex flex-col items-center justify-center gap-2 text-center"
                        data-testid="my-projects-empty"
                    >
                        <Briefcase size={32} weight="duotone" className="text-slate-300" />
                        <div className="font-display text-base text-slate-700">You have no projects assigned yet</div>
                        <div className="text-xs text-slate-500 max-w-md">
                            When Sales or Admin assigns you as a Technical, Commercial or Support owner on a project, it will appear here. You'll also receive an email notification.
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="my-projects-grid">
                        {items.map((p) => (
                            <ProjectCard key={p.id} project={p} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function FilterPill({ active, onClick, label, icon: Icon, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
                active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={testid}
        >
            {Icon && <Icon size={13} weight={active ? "fill" : "regular"} />}
            {label}
        </button>
    );
}

function ProjectCard({ project }) {
    const cs = project.commercial_summary || {};
    return (
        <Link
            to={`/projects/${project.id}`}
            className="reedos-card p-4 hover:border-slate-300 hover:shadow-sm transition-all flex flex-col gap-3"
            data-testid={`my-projects-card-${project.id}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{project.project_number}</div>
                    <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                        <Buildings size={11} /> {project.client_name || "—"}
                    </div>
                </div>
                <ArrowRight size={14} className="text-slate-300 shrink-0 mt-0.5" />
            </div>
            <div className="flex flex-wrap gap-1.5">
                {(project.my_roles || []).map((r) => {
                    const meta = ROLE_META[r];
                    if (!meta) return null;
                    const Icon = meta.icon;
                    return (
                        <span
                            key={r}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${meta.colour}`}
                            data-testid={`my-projects-role-${r}-${project.id}`}
                        >
                            <Icon size={10} weight="bold" />
                            {meta.label}
                        </span>
                    );
                })}
            </div>
            <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                <span>{formatDate(project.created_at)}</span>
                {typeof cs.outstanding_total === "number" && cs.outstanding_total > 0 && (
                    <span className="text-amber-700 font-semibold">₹{formatINR(cs.outstanding_total).replace("₹", "")} outstanding</span>
                )}
            </div>
        </Link>
    );
}
